import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import api from './services/api';

// Suppress known Google OAuth postMessage errors
if (typeof window !== 'undefined') {
  const originalError = console.error;
  console.error = (...args) => {
    // Suppress postMessage cloning errors from Google OAuth
    if (args[0]?.toString?.().includes('postMessage') || 
        args[0]?.toString?.().includes('cloned') ||
        args[0]?.toString?.().includes('Request object could not be cloned')) {
      return;
    }
    originalError.apply(console, args);
  };
}

// Pages
import Landing from './pages/Landing';
import Planning from './pages/Planning';
import Result from './pages/Result';
import Auth from './pages/Auth';
import Profile from './pages/Profile';
import Saved from './pages/Saved';
import Onboarding from './pages/Onboarding';
import Transport from './pages/Transport';
import Hotels from './pages/Hotels';
import Community from './pages/Community';
import Notifications from './pages/Notifications';
import AdminLogin from './pages/AdminLogin';
import Admin from './pages/Admin';
import About from './pages/About';
import Support from './pages/Support';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Payment from './pages/Payment';
import Blog from './pages/Blog';
import FindTravelBuddy from './pages/FindTravelBuddy';
import NotFound from './pages/NotFound';

// Components
import { AuthProvider } from './contexts/AuthContext';
import { LocationProvider } from './contexts/LocationContext';
import SEO from './components/SEO';

// Google OAuth Client ID
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = "985544390641-6nlqho6lbttbo21e9nikibrtckje8ccv.apps.googleusercontent.com";

// Analytics tracker component
function AnalyticsTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Track page view on route change
    api.trackPageView(location.pathname, document.referrer);
  }, [location.pathname]);
  
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <LocationProvider>
            <BrowserRouter>
              <SEO />
              <AnalyticsTracker />
              <div className="min-h-screen bg-[hsl(var(--color-background))]">
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/planning" element={<Planning />} />
                  <Route path="/result" element={<Result />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/saved" element={<Saved />} />
                  <Route path="/onboarding" element={<Onboarding />} />
                  <Route path="/transport" element={<Transport />} />
                  <Route path="/hotels" element={<Hotels />} />
                  <Route path="/community" element={<Community />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/admin-login" element={<AdminLogin />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/payment" element={<Payment />} />
                  <Route path="/payment/result" element={<Payment />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<Blog />} />
                  <Route path="/find-travel-buddy" element={<FindTravelBuddy />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <Toaster 
                  position="top-center" 
                  richColors 
                  toastOptions={{
                    style: {
                      fontFamily: 'Manrope, sans-serif',
                    },
                  }}
                />
              </div>
            </BrowserRouter>
          </LocationProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
